import { useQuery } from '@tanstack/react-query'
import { Table, Tag, Empty, Descriptions, Card, Space, Tooltip } from 'antd'
import { ClockCircleOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { permissionRequestsApi } from '@/api/permission-requests'
import { permissionsApi } from '@/api/permissions'
import type { PermissionRequest, UserPermission, PermissionFlags } from '@/types/permission'

dayjs.extend(relativeTime)

interface UserPendingRequestsProps {
  userId: string
}

export const UserPendingRequests = ({ userId }: UserPendingRequestsProps) => {
  const { data, isLoading } = useQuery({
    queryKey: ['user-pending-requests', userId],
    queryFn: () =>
      permissionRequestsApi.list({
        targetUserId: userId,
        status: 'pending', // Only show pending requests
        page: 1,
        pageSize: 50,
      }),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })

  // Fetch current permissions to compare
  const { data: permissionsData } = useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: () => permissionsApi.getUserPermissions(userId),
  })

  const requests = data?.data?.requests || []
  const currentPermissions = permissionsData?.data?.permissions || []

  // Build a map of current permissions by hospital_id
  const currentPermissionsMap = currentPermissions.reduce(
    (acc, perm) => {
      acc[perm.hospital_id] = perm
      return acc
    },
    {} as Record<string, UserPermission>
  )

  // Get only the NEW permissions being requested (delta from current permissions)
  const getNewPermissionFlags = (flags: string | PermissionFlags, facilityId: string) => {
    if (!flags) return []
    const parsed = typeof flags === 'string' ? JSON.parse(flags) : flags
    const currentPerm = currentPermissionsMap[facilityId]

    const permissions = []

    // Only show permissions that are being added (true in request but not currently active)
    if (parsed.admin && !currentPerm?.admin) {
      permissions.push(
        <Tag key="admin" color="red" icon={<PlusOutlined />}>
          Admin
        </Tag>
      )
    }
    if (parsed.sender && !currentPerm?.sender) {
      permissions.push(
        <Tag key="sender" color="blue" icon={<PlusOutlined />}>
          Sender
        </Tag>
      )
    }
    if (parsed.receiver && !currentPerm?.receiver) {
      permissions.push(
        <Tag key="receiver" color="green" icon={<PlusOutlined />}>
          Receiver
        </Tag>
      )
    }
    if (parsed.readonly && !currentPerm?.readonly) {
      permissions.push(
        <Tag key="readonly" color="default" icon={<PlusOutlined />}>
          Read Only
        </Tag>
      )
    }
    if (parsed.bi && !currentPerm?.bi) {
      permissions.push(
        <Tag key="bi" color="purple" icon={<PlusOutlined />}>
          BI
        </Tag>
      )
    }
    if (parsed.dashboard && !currentPerm?.dashboard) {
      permissions.push(
        <Tag key="dashboard" color="cyan" icon={<PlusOutlined />}>
          Dashboard
        </Tag>
      )
    }
    if (parsed.teamInsights && !currentPerm?.teamInsights) {
      permissions.push(
        <Tag key="teamInsights" color="orange" icon={<PlusOutlined />}>
          Team Insights
        </Tag>
      )
    }

    return permissions
  }

  const columns: ColumnType<PermissionRequest>[] = [
    {
      title: 'Hospital',
      dataIndex: 'facility_name',
      key: 'facility_name',
      width: 200,
      render: (name: string) => (
        <Tooltip title={name}>
          <span style={{ cursor: 'help' }}>{name}</span>
        </Tooltip>
      ),
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: 'Requested Changes',
      dataIndex: 'permission_flags',
      key: 'permission_flags',
      render: (flags: string | PermissionFlags, record: PermissionRequest) => {
        const newPerms = getNewPermissionFlags(flags, record.facility_id)
        if (newPerms.length === 0) {
          return <Tag color="default">No new permissions</Tag>
        }
        return <Space wrap>{newPerms}</Space>
      },
      width: 300,
    },
    {
      title: 'Requested',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format('MMM D, YYYY')}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{dayjs(date).fromNow()}</div>
        </div>
      ),
      width: 150,
      responsive: ['md'],
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format('MMM D, YYYY')}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{dayjs(date).fromNow()}</div>
        </div>
      ),
      width: 150,
      responsive: ['lg'],
    },
  ]

  if (requests.length === 0 && !isLoading) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div>
            <div>No pending permission requests</div>
            <div style={{ marginTop: 8, fontSize: '12px', color: '#888' }}>
              Permission requests will appear here when they require approval
            </div>
          </div>
        }
      />
    )
  }

  return (
    <div>
      {/* Summary Card */}
      {requests.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space>
            <ClockCircleOutlined style={{ color: '#faad14' }} />
            <span style={{ fontWeight: 600 }}>Pending Approval:</span>
            <span>
              {requests.length} {requests.length === 1 ? 'request' : 'requests'}
            </span>
          </Space>
        </Card>
      )}

      {/* Requests Table */}
      <Table
        dataSource={requests}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} requests`,
        }}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (record) => (
            <Descriptions column={1} size="small" bordered>
              {record.reason && (
                <Descriptions.Item label="Reason">{record.reason}</Descriptions.Item>
              )}
              {record.requester_email && (
                <Descriptions.Item label="Requested By">{record.requester_email}</Descriptions.Item>
              )}
              {record.approved_by && (
                <Descriptions.Item label="Approved By">{record.approved_by}</Descriptions.Item>
              )}
              {record.approved_at && (
                <Descriptions.Item label="Approved At">
                  {dayjs(record.approved_at).format('MMM D, YYYY HH:mm')}
                </Descriptions.Item>
              )}
              {record.rejected_reason && (
                <Descriptions.Item label="Rejection Reason">
                  {record.rejected_reason}
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
          rowExpandable: (record) =>
            !!(
              record.reason ||
              record.requester_email ||
              record.approved_by ||
              record.rejected_reason
            ),
        }}
      />
    </div>
  )
}
